'use client';

import { motion } from 'motion/react';

import { cn } from '@/lib/utils';

import { useSim } from './use-sim';

/**
 * The classifier at work, one line at a time: a real-shaped provider error
 * arrives, gets stamped with one of the eight categories, and the stamp
 * decides whether the chain keeps walking.
 */

const SAMPLES = [
  {
    phrase: '"content flagged by our safety system"',
    category: 'moderation',
    stop: true,
  },
  {
    phrase: '"width must be between 300 and 4096"',
    category: 'validation',
    stop: true,
  },
  {
    phrase: 'HTTP 429 · too many requests',
    category: 'rate limited',
    stop: false,
  },
  {
    phrase: 'HTTP 500 · internal error',
    category: 'server error',
    stop: false,
  },
  {
    phrase: 'output was 12KB · expected a video',
    category: 'empty output',
    stop: true,
  },
  {
    phrase: 'no callback after the deadline',
    category: 'timeout',
    stop: false,
  },
] as const;

export function ClassifierLog() {
  const { ref, tick, reduceMotion } = useSim({
    totalTicks: SAMPLES.length * 2 + 3,
    msPerTick: 700,
    loop: true,
  });

  // Each sample takes two ticks: appear, then stamp.
  const t = reduceMotion ? SAMPLES.length * 2 : tick;

  return (
    <div ref={ref} className="mt-4 border border-edge bg-muted/20 p-4">
      <p className="mb-3 font-mono text-[10px] tracking-wider text-muted-foreground uppercase">
        what the provider said → what the engine does
      </p>
      <div
        className="flex min-h-[196px] flex-col gap-2"
        role="img"
        aria-label="Provider error phrases being classified: terminal categories stop the chain, transient ones retry or move to the next provider."
      >
        {SAMPLES.map((sample, index) => {
          const shown = t >= index * 2 + 1;
          const stamped = t >= index * 2 + 2;
          if (!shown) return null;
          return (
            <motion.div
              key={sample.phrase}
              initial={reduceMotion ? false : { opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="flex flex-wrap items-center gap-x-3 gap-y-1"
            >
              <span className="min-w-0 flex-1 truncate font-mono text-[11px] text-muted-foreground">
                {sample.phrase}
              </span>
              {stamped && (
                <motion.span
                  initial={reduceMotion ? false : { opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.25 }}
                  className="flex shrink-0 items-center gap-1.5"
                >
                  <span className="rounded-md border border-edge px-1.5 py-0.5 font-mono text-[9px] tracking-wider text-foreground uppercase">
                    {sample.category}
                  </span>
                  <span
                    className={cn(
                      'rounded-md px-1.5 py-0.5 font-mono text-[9px] tracking-wider uppercase',
                      sample.stop
                        ? 'bg-foreground text-background'
                        : 'border border-edge text-muted-foreground'
                    )}
                  >
                    {sample.stop ? 'stop the chain' : 'retry / next tier'}
                  </span>
                </motion.span>
              )}
            </motion.div>
          );
        })}
      </div>
      <p className="mt-3 border-t border-edge pt-2 font-mono text-[10px] text-muted-foreground tabular-nums">
        8 categories · 127 phrases · each pinned to the production failure that
        forced it
      </p>
    </div>
  );
}
